import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calculator, Save, Download, Loader, RefreshCw, AlertTriangle, CheckCircle2,
  Truck, Fuel, Users, Package, Building2, Camera, Trash2, RotateCcw, Info,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  defaultModel, runModel, compareVehicles, LINE_DEFS, VEHICLE_PRESETS,
} from '../utils/tenderModel';
import { exportSubmission } from '../utils/tenderExport';

// Tender Cost Model — the operating-cost test run behind a price submission.
//
// The page answers one question in one number: at our real volumes and a real
// cost base, what must each of the four priced lines charge to land on Australia
// Post's recognised 5% profit — and how far is that from what they pay today?
//
// Four tabs, in the order the argument is built:
//   Result   — the answer, the four prices, and where every dollar of cost went
//   Cost base — labour, vehicles, fuel, equipment, overhead: every input, editable
//   Vehicles — the HiAce and the Deliver 9 run through the identical model
//   Volumes  — what we deliver and what AP pays for it today
//
// Nothing is hard-coded that a tender panel might ask about: every figure on the
// Cost base tab is an input, and the Assumptions sheet of the export lists them
// all with their values. Live actuals (volumes, drivers a day, hours, km, and the
// driver payments actually going out the door) come from tender-model-sb so the
// starting point is the business as it runs, not as anyone remembers it.

const API = '/.netlify/functions/tender-model-sb';
const api = (action, payload = {}) =>
  fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then((r) => r.json());

const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
const AUD2 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const $0 = (v) => (Number.isFinite(Number(v)) ? AUD.format(Number(v)) : '—');
const $2 = (v) => (Number.isFinite(Number(v)) ? AUD2.format(Number(v)) : '—');
const $4 = (v) => (Number.isFinite(Number(v)) ? `$${Number(v).toFixed(4)}` : '—');
const n0 = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v)).toLocaleString('en-AU') : '—');
const n1 = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—');
const pct = (v, dp = 1) => (Number.isFinite(Number(v)) ? `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(dp)}%` : '—');
const pct0 = (v, dp = 1) => (Number.isFinite(Number(v)) ? `${Number(v).toFixed(dp)}%` : '—');
const fmtWhen = (s) => (s ? new Date(s).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16 };
const label = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const sectionTitle = { fontSize: '.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-gold)', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: '.85rem', borderBottom: '1px solid var(--hp-border)' };
const num = { ...td, textAlign: 'right', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
const scrollBox = { overflowX: 'auto', WebkitOverflowScrolling: 'touch' };
const wide = { width: '100%', boxSizing: 'border-box' };
const grid = (min = 180) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12 });

// A labelled numeric input. Values are held as typed — the model coerces — so a
// half-finished "8." never snaps back to 8 under the cursor.
const Field = ({ title, value, onChange, suffix, hint, step = 'any', type = 'number' }) => (
  <div>
    <span style={label}>{title}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        className="input" style={{ ...wide, fontFamily: 'var(--font-mono)' }}
        type={type} step={step} value={value === undefined || value === null ? '' : value}
        onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
      />
      {suffix && <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>{suffix}</span>}
    </div>
    {hint && <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)', marginTop: 3, lineHeight: 1.35 }}>{hint}</div>}
  </div>
);

const Stat = ({ icon: Icon, title, value, sub, tone }) => (
  <div style={{ ...card, flex: '1 1 190px', minWidth: 170 }}>
    <div style={{ ...label, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
      {Icon && <Icon size={12} />}{title}
    </div>
    <div style={{ fontSize: '1.45rem', fontWeight: 800, fontFamily: 'var(--font-mono)', color: tone || 'var(--hp-text)', lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 4, lineHeight: 1.4 }}>{sub}</div>}
  </div>
);

const Subtotal = ({ value, of }) => (
  <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--hp-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '.8rem' }}>
    <span style={{ color: 'var(--hp-dim)', fontWeight: 700 }}>Subtotal</span>
    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: 'var(--hp-gold)' }}>
      {$0(value)}{of > 0 ? <span style={{ color: 'var(--hp-dim)', fontWeight: 600 }}> · {pct0((value / of) * 100)} of cost</span> : null}
    </span>
  </div>
);

export default function TenderModelPage() {
  const { addNotification } = useApp();
  const notify = useCallback((msg, type = 'success') => {
    if (addNotification) addNotification(msg, type); else if (type === 'error') console.error(msg);
  }, [addNotification]);

  const [tab, setTab] = useState('result');
  const [model, setModel] = useState(null);
  const [actuals, setActuals] = useState(null);
  const [annual, setAnnual] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api('load');
        if (!alive) return;
        if (!r.success) throw new Error(r.error || 'Load failed');
        // A stored model wins, but new keys added to the seed since it was saved
        // still fill in — otherwise a deploy that adds a cost line would leave an
        // old run with an undefined input silently costing nothing.
        setModel(r.model ? mergeModel(defaultModel(), r.model) : defaultModel());
        setActuals(r.actuals && !r.actuals.error ? r.actuals : null);
        setAnnual(r.annual && !r.annual.error ? r.annual : null);
        setSnapshots(r.snapshots || []);
        setUpdated(r.updated || null);
        if (r.actuals && r.actuals.error) setErr(`Live actuals unavailable: ${r.actuals.error}`);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const set = useCallback((patch) => { setModel((m) => ({ ...m, ...patch })); setDirty(true); }, []);
  const setIn = useCallback((section, patch) => {
    setModel((m) => ({ ...m, [section]: { ...m[section], ...patch } }));
    setDirty(true);
  }, []);
  const setLine = useCallback((key, patch) => {
    setModel((m) => ({ ...m, lines: { ...m.lines, [key]: { ...m.lines[key], ...patch } } }));
    setDirty(true);
  }, []);
  const setProfile = useCallback((key, patch) => {
    setModel((m) => ({ ...m, fleet: { ...m.fleet, profiles: { ...m.fleet.profiles, [key]: { ...m.fleet.profiles[key], ...patch } } } }));
    setDirty(true);
  }, []);

  const run = useMemo(() => (model ? runModel(model) : null), [model]);
  const compare = useMemo(() => (model ? compareVehicles(model) : null), [model]);

  const save = async () => {
    setSaving(true);
    const r = await api('save', { model });
    setSaving(false);
    if (r.success) { setUpdated(r.updated); setDirty(false); notify('Model saved'); }
    else notify(r.error || 'Save failed', 'error');
  };

  const snapshot = async () => {
    const name = window.prompt('Name this run', model.name || 'Test run');
    if (name === null) return;
    const r = await api('snapshot', { name, model });
    if (r.success) { setSnapshots(r.snapshots); notify(`Snapshot "${name}" saved`); }
    else notify(r.error || 'Snapshot failed', 'error');
  };

  const restore = async (s) => {
    if (!window.confirm(`Load "${s.name}" from ${fmtWhen(s.at)}? The working model on screen is replaced — snapshot it first if you want to keep it.`)) return;
    const r = await api('restoreSnapshot', { id: s.id });
    if (r.success) { setModel(mergeModel(defaultModel(), r.model)); setDirty(true); notify(`Loaded "${r.name}"`); }
    else notify(r.error || 'Restore failed', 'error');
  };

  const removeSnapshot = async (s) => {
    if (!window.confirm(`Delete the snapshot "${s.name}"? This cannot be undone.`)) return;
    const r = await api('deleteSnapshot', { id: s.id });
    if (r.success) { setSnapshots(r.snapshots); notify('Snapshot deleted'); }
    else notify(r.error || 'Delete failed', 'error');
  };

  const reseed = async () => {
    if (!window.confirm('Reset every input back to the seeded defaults? Your saved snapshots are untouched.')) return;
    setModel(defaultModel()); setDirty(true); notify('Model reset to defaults');
  };

  const doExport = () => {
    try { const name = exportSubmission(model, run, compare); notify(`Exported ${name}`); }
    catch (e) { notify(e.message || 'Export failed', 'error'); }
  };

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: 'var(--hp-dim)' }}>
      <Loader size={22} className="spin" /><div style={{ marginTop: 10, fontSize: '.85rem' }}>Loading the model and the live actuals…</div>
    </div>;
  }
  if (!model) {
    return <div style={{ ...card, borderColor: 'var(--hp-red)', color: 'var(--hp-red)' }}>{err || 'Could not load the model.'}</div>;
  }

  const TABS = [
    ['result', 'Result', Calculator],
    ['costs', 'Cost base', Building2],
    ['vehicles', 'Vehicles', Truck],
    ['volumes', 'Volumes & rates', Package],
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ minWidth: 260, flex: '1 1 320px' }}>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calculator size={18} style={{ color: 'var(--hp-gold)' }} /> Tender Cost Model
          </h2>
          <input
            className="input" style={{ ...wide, maxWidth: 420, fontWeight: 700 }}
            value={model.name || ''} onChange={(e) => set({ name: e.target.value })}
            placeholder="Name this run"
          />
          <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 5 }}>
            Operating cost test run · all figures ex GST · {updated ? `saved ${fmtWhen(updated.at)} by ${updated.by}` : 'not saved yet'}
            {dirty && <span style={{ color: 'var(--hp-amber)', fontWeight: 700 }}> · unsaved changes</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <button className="btn btn-sm" onClick={reseed}><RotateCcw size={13} /> Reset</button>
          <button className="btn btn-sm" onClick={snapshot}><Camera size={13} /> Snapshot</button>
          <button className="btn btn-sm" onClick={doExport}><Download size={13} /> Export .xlsx</button>
          <button className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
            {saving ? <Loader size={13} className="spin" /> : <Save size={13} />} Save
          </button>
        </div>
      </div>

      {err && (
        <div style={{ ...card, borderColor: 'var(--hp-amber)', color: 'var(--hp-amber)', fontSize: '.8rem', marginBottom: 14, display: 'flex', gap: 8 }}>
          <AlertTriangle size={15} /> {err}
        </div>
      )}

      {/* the two controls that change everything, always in reach */}
      <div style={{ ...card, marginBottom: 14, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <span style={label}>Vehicle</span>
          <div style={{ display: 'flex', background: 'var(--hp-surface)', borderRadius: 8, padding: 3, border: '1px solid var(--hp-border)' }}>
            {Object.keys(model.fleet.profiles).map((k) => (
              <button key={k} onClick={() => setIn('fleet', { vehicle: k })}
                style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '.75rem', fontWeight: 700,
                  background: model.fleet.vehicle === k ? 'var(--hp-gold)' : 'transparent',
                  color: model.fleet.vehicle === k ? 'var(--hp-black)' : 'var(--hp-dim)' }}>
                {model.fleet.profiles[k].label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: 120 }}>
          <Field title="Profit target" value={model.margin.pct} onChange={(v) => setIn('margin', { pct: v })} suffix="%" />
        </div>
        <div style={{ width: 170 }}>
          <span style={label}>Measured on</span>
          <select className="input" style={wide} value={model.margin.basis} onChange={(e) => setIn('margin', { basis: e.target.value })}>
            <option value="revenue">Revenue (AP basis)</option>
            <option value="cost">Mark-up on cost</option>
          </select>
        </div>
        <div style={{ width: 110 }}>
          <Field title="Vans" value={model.fleet.vans} onChange={(v) => setIn('fleet', { vans: v })} suffix="on road" />
        </div>
        <div style={{ flex: '1 1 200px', fontSize: '.72rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
          {model.margin.basis === 'revenue'
            ? `${model.margin.pct}% of every dollar invoiced — required revenue is cost ÷ ${(1 - Number(model.margin.pct) / 100).toFixed(2)}, a ${(100 / (100 - Number(model.margin.pct)) * 100 - 100).toFixed(2)}% mark-up on cost.`
            : `${model.margin.pct}% added to cost — that is a ${(Number(model.margin.pct) / (100 + Number(model.margin.pct)) * 100).toFixed(2)}% margin on revenue, less than AP's basis.`}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid var(--hp-border)', marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(([id, lbl, Icon]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: '9px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '.8rem', fontWeight: 700,
              color: tab === id ? 'var(--hp-gold)' : 'var(--hp-dim)',
              borderBottom: `2px solid ${tab === id ? 'var(--hp-gold)' : 'transparent'}`, marginBottom: -1,
              display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon size={13} /> {lbl}
          </button>
        ))}
      </div>

      {tab === 'result' && <ResultTab model={model} run={run} actuals={actuals} annual={annual} setIn={setIn} snapshots={snapshots} onRestore={restore} onDeleteSnapshot={removeSnapshot} />}
      {tab === 'costs' && <CostTab model={model} run={run} actuals={actuals} setIn={setIn} setProfile={setProfile} />}
      {tab === 'vehicles' && <VehicleTab model={model} compare={compare} actuals={actuals} setIn={setIn} setProfile={setProfile} />}
      {tab === 'volumes' && <VolumeTab model={model} run={run} actuals={actuals} annual={annual} setLine={setLine} setIn={setIn} />}
    </div>
  );
}

// Fill any key the stored model predates, one level into each section, so an old
// saved run picks up newly added inputs instead of costing them at zero.
function mergeModel(seed, stored) {
  const out = { ...seed, ...stored };
  for (const k of ['margin', 'ops', 'labour', 'fleet', 'fuel', 'equipment', 'overheads', 'otherRevenue']) {
    out[k] = { ...seed[k], ...(stored[k] || {}) };
  }
  out.fleet.profiles = { ...seed.fleet.profiles };
  for (const v of Object.keys(seed.fleet.profiles)) {
    out.fleet.profiles[v] = { ...seed.fleet.profiles[v], ...((stored.fleet && stored.fleet.profiles && stored.fleet.profiles[v]) || {}) };
  }
  out.lines = { ...seed.lines };
  for (const l of Object.keys(seed.lines)) out.lines[l] = { ...seed.lines[l], ...((stored.lines && stored.lines[l]) || {}) };
  return out;
}

// ── Result ───────────────────────────────────────────────────────────────────
function ResultTab({ model, run, actuals, annual, setIn, snapshots, onRestore, onDeleteSnapshot }) {
  const p = run.pricing;
  const c = run.costs;
  const clears = p.headroom >= 0;
  const target = Number(model.margin.pct);

  // What the depot pays drivers today, annualised off the last complete RCTI
  // periods. Today's drivers are subcontractors — they bring their own labour and
  // most of their own running costs — so this is the sanity check on the labour
  // build-up, not a like-for-like cost.
  const rcti = (actuals && actuals.rctiPeriods) || [];
  const rctiRef = rcti.length ? rcti[0] : null;
  const rctiAnnual = rctiRef ? rctiRef.gross * 13 : null;
  // Like for like: everything it costs to put a driver and a van on the road, both
  // ways. A subcontractor's payment already covers their own running costs, so the
  // in-house side has to carry the vans and the fuel to be the same comparison —
  // and the van lease we recover from them comes back off their side.
  const allIn = c.labour.total + c.vehicles.total + c.fuel.total;
  const subbieNet = rctiRef ? (rctiRef.gross - rctiRef.lease) * 13 : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Stat icon={Building2} title="Operating cost" value={$0(c.total)} sub={`${$2(run.costPerDriverDay)} per driver day · ${$4(run.costPerParcel)} per article`} tone="var(--hp-gold)" />
        <Stat icon={Package} title="Revenue at today's rates" value={$0(p.currentRevenue)} sub={`${n0(run.volumes.parcels)} articles + ${n0(run.volumes.depot)} bags + ${n0(run.volumes.doe)} DoE days over ${n0(run.volumes.days)} delivery days`} />
        <Stat icon={Calculator} title="Margin at today's rates" value={pct0(p.currentMarginPct, 2)}
          sub={`${$0(p.currentProfit)} on ${$0(p.currentRevenue)}`}
          tone={p.currentMarginPct >= target ? 'var(--hp-green)' : 'var(--hp-red)'} />
        <Stat icon={CheckCircle2} title={clears ? 'Headroom' : 'Uplift needed'}
          value={clears ? $0(p.costHeadroom) : pct(p.blendedUpliftPct, 2)}
          sub={clears ? `cost could rise this much and still make ${target}%` : `blended across all lines to reach ${target}%`}
          tone={clears ? 'var(--hp-green)' : 'var(--hp-amber)'} />
      </div>

      {/* the verdict, said out loud */}
      <div style={{ ...card, borderColor: clears ? 'var(--hp-green)' : 'var(--hp-amber)' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          {clears ? <CheckCircle2 size={18} style={{ color: 'var(--hp-green)', flexShrink: 0, marginTop: 2 }} />
                  : <AlertTriangle size={18} style={{ color: 'var(--hp-amber)', flexShrink: 0, marginTop: 2 }} />}
          <div style={{ fontSize: '.86rem', lineHeight: 1.6 }}>
            {clears ? (
              <>
                <b>Today's rates already clear {target}%.</b> On this cost base the contract runs at{' '}
                <b style={{ fontFamily: 'var(--font-mono)' }}>{pct0(p.currentMarginPct, 2)}</b>, which is{' '}
                <b style={{ fontFamily: 'var(--font-mono)' }}>{$0(p.headroom)}</b> of revenue more than the {target}% target needs.
                Costs would have to rise <b style={{ fontFamily: 'var(--font-mono)' }}>{$0(p.costHeadroom)}</b> a year
                — {pct0((p.costHeadroom / c.total) * 100)} of the cost base — before the submission stopped clearing it.
                So the submission holds the current rates: every line is floored at what AP pays today and nothing needs to rise.
                Load the overheads and contingencies you actually expect and this number tells you how much of it the contract carries.
              </>
            ) : (
              <>
                <b>Today's rates fall short of {target}%.</b> The cost base needs{' '}
                <b style={{ fontFamily: 'var(--font-mono)' }}>{$0(p.required)}</b> of revenue and today's rates on these volumes
                bring in <b style={{ fontFamily: 'var(--font-mono)' }}>{$0(p.currentRevenue)}</b> — a shortfall of{' '}
                <b style={{ fontFamily: 'var(--font-mono)' }}>{$0(-p.headroom)}</b>, which is{' '}
                <b style={{ fontFamily: 'var(--font-mono)' }}>{pct(p.blendedUpliftPct, 2)}</b> across the whole submission.
                The prices below spread that in proportion to each line's weight, and no line is ever priced below what AP pays today.
              </>
            )}
          </div>
        </div>
      </div>

      {/* the submission */}
      <div style={card}>
        <h3 style={sectionTitle}><Package size={13} /> The submission — four lines, ex GST</h3>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead><tr>
              <th style={th}>Line</th><th style={{ ...th, textAlign: 'right' }}>Annual volume</th>
              <th style={{ ...th, textAlign: 'right' }}>Rate now</th><th style={{ ...th, textAlign: 'right' }}>Proposed</th>
              <th style={{ ...th, textAlign: 'right' }}>Move</th>
              <th style={{ ...th, textAlign: 'right' }}>Revenue now</th><th style={{ ...th, textAlign: 'right' }}>Proposed</th>
            </tr></thead>
            <tbody>
              {p.lines.map((l) => (
                <tr key={l.key} style={{ opacity: l.include ? 1 : 0.4 }}>
                  <td style={td}>
                    <div style={{ fontWeight: 700 }}>{l.label}</div>
                    <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)' }}>per {l.unit}{l.atFloor ? ' · held at current rate' : ''}</div>
                  </td>
                  <td style={num}>{n0(l.volume)}</td>
                  <td style={num}>{l.currentRate >= 100 ? $2(l.currentRate) : $4(l.currentRate)}</td>
                  <td style={{ ...num, fontWeight: 800, color: 'var(--hp-gold)' }}>{l.proposedRate >= 100 ? $2(l.proposedRate) : $4(l.proposedRate)}</td>
                  <td style={{ ...num, color: l.upliftPct > 0.005 ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>{l.upliftPct > 0.005 ? pct(l.upliftPct, 2) : 'hold'}</td>
                  <td style={num}>{$0(l.currentRevenue)}</td>
                  <td style={num}>{$0(l.proposedRevenue)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...td, fontWeight: 800 }}>Total</td>
                <td style={num} /><td style={num} /><td style={num} />
                <td style={{ ...num, fontWeight: 800, color: p.blendedUpliftPct > 0.005 ? 'var(--hp-amber)' : 'var(--hp-green)' }}>
                  {p.blendedUpliftPct > 0.005 ? pct(p.blendedUpliftPct, 2) : 'hold'}
                </td>
                <td style={{ ...num, fontWeight: 800 }}>{$0(p.currentRevenue)}</td>
                <td style={{ ...num, fontWeight: 800, color: 'var(--hp-gold)' }}>{$0(p.proposedRevenue)}</td>
              </tr>
              <tr>
                <td style={{ ...td, color: 'var(--hp-dim)' }}>less operating cost</td>
                <td style={num} /><td style={num} /><td style={num} /><td style={num} />
                <td style={{ ...num, color: 'var(--hp-dim)' }}>{$0(-c.total)}</td>
                <td style={{ ...num, color: 'var(--hp-dim)' }}>{$0(-c.total)}</td>
              </tr>
              <tr>
                <td style={{ ...td, fontWeight: 800, borderBottom: 'none' }}>Profit</td>
                <td style={{ ...num, borderBottom: 'none' }} /><td style={{ ...num, borderBottom: 'none' }} />
                <td style={{ ...num, borderBottom: 'none' }} /><td style={{ ...num, borderBottom: 'none' }} />
                <td style={{ ...num, borderBottom: 'none', fontWeight: 800, color: p.currentProfit >= 0 ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                  {$0(p.currentProfit)}<div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--hp-dim)' }}>{pct0(p.currentMarginPct, 2)}</div>
                </td>
                <td style={{ ...num, borderBottom: 'none', fontWeight: 800, color: 'var(--hp-green)' }}>
                  {$0(p.proposedProfit)}<div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--hp-dim)' }}>{pct0(p.proposedMarginPct, 2)}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 10, lineHeight: 1.5 }}>
          DoE also carries {n0(model.lines.doe.collectionsPerYear)} school collections a year at {$4(model.lines.doe.collectionRate)},
          which move with the day rate. 2nd Wave Express is priced on the <b>net</b> {$2(model.lines.express.currentRate)} —
          the {$2(model.lines.express.grossRate)} unit price less the {$2(Math.abs(model.lines.express.adjustment))} per-item adjustment.
        </div>
      </div>

      {/* where the money goes */}
      <div style={card}>
        <h3 style={sectionTitle}><Building2 size={13} /> Where the cost sits</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {c.groups.map((g) => {
            const share = c.total > 0 ? (g.total / c.total) * 100 : 0;
            return (
              <div key={g.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', marginBottom: 3 }}>
                  <span style={{ fontWeight: 700 }}>{g.label}</span>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{$0(g.total)} <span style={{ color: 'var(--hp-dim)' }}>{pct0(share)}</span></span>
                </div>
                <div style={{ height: 7, background: 'var(--hp-surface)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(100, share)}%`, height: '100%', background: 'var(--hp-gold)', opacity: 0.85 }} />
                </div>
              </div>
            );
          })}
        </div>
        <Subtotal value={c.total} of={0} />
      </div>

      {/* does the priced revenue add up to the company's actual turnover? */}
      <div style={card}>
        <h3 style={sectionTitle}><Package size={13} /> Turnover check — one year, everything the company invoices</h3>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
            <tbody>
              {p.lines.filter((l) => l.include).map((l) => (
                <tr key={l.key}><td style={td}>{l.label}</td><td style={num}>{$0(l.currentRevenue)}</td></tr>
              ))}
              <tr>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{(model.otherRevenue && model.otherRevenue.label) || 'Other revenue'}</span>
                    <input className="input" style={{ width: 130, fontFamily: 'var(--font-mono)' }} type="number"
                      value={(model.otherRevenue && model.otherRevenue.annual) ?? 0}
                      onChange={(e) => setIn('otherRevenue', { annual: e.target.value })} />
                  </div>
                  <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                    not priced here and not in the cost base — sorters, not these drivers
                  </div>
                </td>
                <td style={num}>{$0(run.turnover.other)}</td>
              </tr>
              <tr>
                <td style={{ ...td, fontWeight: 800, borderBottom: 'none' }}>Total turnover, ex GST</td>
                <td style={{ ...num, fontWeight: 800, borderBottom: 'none', color: 'var(--hp-gold)', fontSize: '1rem' }}>{$0(run.turnover.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 12, lineHeight: 1.55, display: 'flex', gap: 8 }}>
          <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            The four priced lines come to <b style={{ fontFamily: 'var(--font-mono)' }}>{$0(run.turnover.priced)}</b> on
            {' '}{n0(run.volumes.parcels)} articles over {n0(run.volumes.days)} delivery days. If the company's real turnover is
            higher than the total above, the difference is revenue this model is not pricing — put it in the row above so the
            two reconcile, and remember its cost is not in the cost base either.
          </span>
        </div>
      </div>

      {/* is this cost base believable? */}
      {rctiRef && (
        <div style={card}>
          <h3 style={sectionTitle}><Users size={13} /> Sanity check — two ways to put a driver and a van on the road</h3>
          <div style={{ ...grid(200) }}>
            <div>
              <span style={label}>Employ &amp; own — this model</span>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.15rem', fontWeight: 800 }}>{$0(allIn)}</div>
              <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
                labour {$0(c.labour.total)} + vans {$0(c.vehicles.total)} + fuel {$0(c.fuel.total)}
              </div>
            </div>
            <div>
              <span style={label}>Subcontract — what we do today</span>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.15rem', fontWeight: 800 }}>{$0(subbieNet)}</div>
              <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
                {$0(rctiRef.gross)} to {rctiRef.drivers} drivers in the period to {rctiRef.periodEnd},
                less {$0(rctiRef.lease)} of van lease recovered, × 13
              </div>
            </div>
            <div>
              <span style={label}>What employing costs</span>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.15rem', fontWeight: 800, color: allIn > subbieNet ? 'var(--hp-amber)' : 'var(--hp-green)' }}>
                {allIn >= subbieNet ? '+' : ''}{$0(allIn - subbieNet)}
              </div>
              <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>
                {pct(((allIn / subbieNet) - 1) * 100)} a year · {$0((allIn - subbieNet) / Math.max(1, Number(model.fleet.vans)))} per van
              </div>
            </div>
            <div>
              <span style={label}>Labour alone vs today</span>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.15rem', fontWeight: 800 }}>{pct(((c.labour.total / rctiAnnual) - 1) * 100)}</div>
              <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>
                {$0(c.labour.total)} modelled vs {$0(rctiAnnual)} paid
              </div>
            </div>
          </div>
          <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 12, lineHeight: 1.55, display: 'flex', gap: 8 }}>
            <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              The left-hand column is the whole cost of doing the work in-house — award labour, on-costs, the vans and the
              diesel. The right-hand column is what leaves the bank today, because a subcontractor's payment already covers
              their labour <i>and</i> their running costs; the van lease we recover from them is netted off so the two sides
              count the same things. The difference is the price of the change, and it is a number the tender should be
              carrying deliberately rather than discovering later. Driver counts differ slightly between the two —
              {' '}{n1(model.ops.driversPerDay)} modelled a day against {rctiRef.drivers} paid in the period — so read it as
              a scale, not a decimal-place answer.
            </span>
          </div>
        </div>
      )}

      {snapshots.length > 0 && (
        <div style={card}>
          <h3 style={sectionTitle}><Camera size={13} /> Saved runs</h3>
          <div style={scrollBox}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead><tr><th style={th}>Run</th><th style={th}>Saved</th><th style={th}>By</th><th style={th} /></tr></thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.id}>
                    <td style={{ ...td, fontWeight: 700 }}>{s.name}</td>
                    <td style={{ ...td, color: 'var(--hp-dim)' }}>{fmtWhen(s.at)}</td>
                    <td style={{ ...td, color: 'var(--hp-dim)' }}>{s.by}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm" onClick={() => onRestore(s)}>Load</button>{' '}
                      <button className="btn btn-sm" onClick={() => onDeleteSnapshot(s)} title="Delete"><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Cost base ────────────────────────────────────────────────────────────────
function CostTab({ model, run, actuals, setIn, setProfile }) {
  const c = run.costs;
  const L = c.labour;
  const V = c.vehicles;
  const F = c.fuel;
  const E = c.equipment;
  const veh = model.fleet.profiles[model.fleet.vehicle];
  const sh = actuals && actuals.shifts;

  const addOverhead = () => setIn('overheads', { rows: [...(model.overheads.rows || []), { label: 'New overhead', annual: 0 }] });
  const setOverhead = (i, patch) => {
    const rows = (model.overheads.rows || []).map((r, j) => (j === i ? { ...r, ...patch } : r));
    setIn('overheads', { rows });
  };
  const dropOverhead = (i) => setIn('overheads', { rows: (model.overheads.rows || []).filter((_, j) => j !== i) });

  const addEquip = () => setIn('equipment', { rows: [...(model.equipment.rows || []), { label: 'New item', perDriverPerYear: 0 }] });
  const setEquip = (i, patch) => setIn('equipment', { rows: (model.equipment.rows || []).map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const dropEquip = (i) => setIn('equipment', { rows: (model.equipment.rows || []).filter((_, j) => j !== i) });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* operations */}
      <div style={card}>
        <h3 style={sectionTitle}><Users size={13} /> Operations — the shape of the year</h3>
        <div style={grid(160)}>
          <Field title="Weekday operating days" value={model.ops.weekdayDays} onChange={(v) => setIn('ops', { weekdayDays: v })} hint="Mon–Fri less NSW public holidays" />
          <Field title="Saturday operating days" value={model.ops.saturdayDays} onChange={(v) => setIn('ops', { saturdayDays: v })} />
          <Field title="Drivers per weekday" value={model.ops.driversPerDay} onChange={(v) => setIn('ops', { driversPerDay: v })}
            hint={sh ? `live: ${n1(sh.driversPerDay)} a day` : null} />
          <Field title="Drivers per Saturday" value={model.ops.satDriversPerDay} onChange={(v) => setIn('ops', { satDriversPerDay: v })} />
          <Field title="Hours per shift" value={model.ops.hoursPerShift} onChange={(v) => setIn('ops', { hoursPerShift: v })}
            hint={sh ? `live: mean ${n1(sh.hoursMean)}, median ${n1(sh.hoursMedian)}` : null} />
          <Field title="Overtime hours per shift" value={model.ops.otHoursPerShift} onChange={(v) => setIn('ops', { otHoursPerShift: v })} hint="beyond the ordinary hours above" />
          <Field title="Saturday hours" value={model.ops.satHoursPerShift} onChange={(v) => setIn('ops', { satHoursPerShift: v })} />
        </div>
        <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: 12 }}>
          {n0(L.driverDays)} driver days a year · {n0(L.hoursPerYear)} paid hours
        </div>
      </div>

      {/* labour */}
      <div style={card}>
        <h3 style={sectionTitle}><Users size={13} /> Driver labour — casual Grade 2</h3>
        <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.5 }}>
          Road Transport and Distribution Award 2020 [MA000038], Grade 2, from 1 July 2026.
          Ordinary hours carry the casual loading; overtime swaps it for the award's 10% casual overtime loading plus the penalty.
          Penalties do not compound — the higher one applies.
        </div>
        <div style={grid(160)}>
          <Field title="Grade 2 base hourly" value={model.labour.baseRate} onChange={(v) => setIn('labour', { baseRate: v })} suffix="/hr" />
          <Field title="Casual loading" value={model.labour.casualLoadingPct} onChange={(v) => setIn('labour', { casualLoadingPct: v })} suffix="%" />
          <Field title="Saturday penalty" value={model.labour.satPenaltyPct} onChange={(v) => setIn('labour', { satPenaltyPct: v })} suffix="%" />
          <Field title="Overtime — first 2 h" value={model.labour.otFirst2Pct} onChange={(v) => setIn('labour', { otFirst2Pct: v })} suffix="%" />
          <Field title="Overtime — thereafter" value={model.labour.otAfterPct} onChange={(v) => setIn('labour', { otAfterPct: v })} suffix="%" />
          <Field title="Allowance per shift" value={model.labour.allowancePerShift} onChange={(v) => setIn('labour', { allowancePerShift: v })} suffix="$" hint="meal, tool or any site allowance" />
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '14px 0', fontSize: '.8rem' }}>
          <div><span style={label}>Casual ordinary</span><b style={{ fontFamily: 'var(--font-mono)' }}>{$2(L.rates.casual)}</b>/hr</div>
          <div><span style={label}>Casual Saturday</span><b style={{ fontFamily: 'var(--font-mono)' }}>{$2(L.rates.saturday)}</b>/hr</div>
          <div><span style={label}>Overtime first 2 h</span><b style={{ fontFamily: 'var(--font-mono)' }}>{$2(L.rates.otFirst2)}</b>/hr</div>
          <div><span style={label}>Cost of one shift</span><b style={{ fontFamily: 'var(--font-mono)' }}>{$2(L.weekdayShift)}</b></div>
          <div><span style={label}>All-in effective</span><b style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{$2(L.effectiveHourly)}</b>/hr</div>
        </div>

        <h4 style={{ ...sectionTitle, color: 'var(--hp-dim)', fontSize: '.72rem', margin: '16px 0 10px' }}>On-costs</h4>
        <div style={grid(160)}>
          <Field title="Superannuation" value={model.labour.superPct} onChange={(v) => setIn('labour', { superPct: v })} suffix="%" hint="12% from 1 Jul 2025" />
          <Field title="Workers compensation" value={model.labour.workersCompPct} onChange={(v) => setIn('labour', { workersCompPct: v })} suffix="%" hint="icare NSW road freight — confirm against the policy" />
          <Field title="Payroll tax" value={model.labour.payrollTaxPct} onChange={(v) => setIn('labour', { payrollTaxPct: v })} suffix="%" hint="NSW 5.45%" />
          <Field title="Payroll tax threshold" value={model.labour.payrollTaxThreshold} onChange={(v) => setIn('labour', { payrollTaxThreshold: v })} suffix="$" />
          <div>
            <span style={label}>Apply payroll tax</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.8rem', paddingTop: 6 }}>
              <input type="checkbox" checked={model.labour.payrollTaxOn !== false} onChange={(e) => setIn('labour', { payrollTaxOn: e.target.checked })} />
              on wages + super above the threshold
            </label>
          </div>
        </div>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, minWidth: 380 }}>
            <tbody>
              {[['Wages', L.wages], ['Superannuation', L.super], ['Workers compensation', L.workersComp], ['Payroll tax', L.payrollTax]].map(([k, v]) => (
                <tr key={k}><td style={td}>{k}</td><td style={num}>{$0(v)}</td>
                  <td style={{ ...num, color: 'var(--hp-dim)', fontSize: '.72rem' }}>{pct0((v / L.total) * 100)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <Subtotal value={L.total} of={c.total} />
      </div>

      {/* vehicles */}
      <div style={card}>
        <h3 style={sectionTitle}><Truck size={13} /> Vehicles — {veh.label}</h3>
        <div style={grid(160)}>
          <Field title="Vans on the road" value={model.fleet.vans} onChange={(v) => setIn('fleet', { vans: v })} />
          <Field title="Spare vans" value={model.fleet.spareVans} onChange={(v) => setIn('fleet', { spareVans: v })} hint="swaps and services — cost, no driver" />
          <div>
            <span style={label}>Ownership</span>
            <select className="input" style={wide} value={model.fleet.ownership} onChange={(e) => setIn('fleet', { ownership: e.target.value })}>
              <option value="finance">Buy & finance</option>
              <option value="lease">Lease</option>
            </select>
          </div>
          {model.fleet.ownership === 'lease' ? (
            <Field title="Lease per van" value={model.fleet.leasePerVanPerMonth} onChange={(v) => setIn('fleet', { leasePerVanPerMonth: v })} suffix="$/month" />
          ) : (
            <>
              <Field title="Capital cost" value={veh.capex} onChange={(v) => setProfile(model.fleet.vehicle, { capex: v })} suffix="$" hint="drive away, incl. fit-out" />
              <Field title="Residual at end of term" value={veh.residualPct} onChange={(v) => setProfile(model.fleet.vehicle, { residualPct: v })} suffix="%" />
              <Field title="Term" value={model.fleet.termYears} onChange={(v) => setIn('fleet', { termYears: v })} suffix="years" />
              <Field title="Finance rate" value={model.fleet.financeRatePct} onChange={(v) => setIn('fleet', { financeRatePct: v })} suffix="%" hint="interest on the average balance" />
            </>
          )}
          <Field title="Registration & CTP" value={veh.regoCtp} onChange={(v) => setProfile(model.fleet.vehicle, { regoCtp: v })} suffix="$/yr" />
          <Field title="Insurance" value={veh.insurance} onChange={(v) => setProfile(model.fleet.vehicle, { insurance: v })} suffix="$/yr" />
          <Field title="Service interval — km" value={veh.serviceIntervalKm} onChange={(v) => setProfile(model.fleet.vehicle, { serviceIntervalKm: v })} suffix="km" />
          <Field title="Service interval — time" value={veh.serviceIntervalMonths} onChange={(v) => setProfile(model.fleet.vehicle, { serviceIntervalMonths: v })} suffix="months" hint="whichever comes first" />
          <Field title="Cost per service" value={veh.serviceCost} onChange={(v) => setProfile(model.fleet.vehicle, { serviceCost: v })} suffix="$" />
          <Field title="Tyre set" value={veh.tyreSetCost} onChange={(v) => setProfile(model.fleet.vehicle, { tyreSetCost: v })} suffix="$" />
          <Field title="Tyre life" value={veh.tyreLifeKm} onChange={(v) => setProfile(model.fleet.vehicle, { tyreLifeKm: v })} suffix="km" />
          <Field title="Repairs & maintenance" value={veh.repairs} onChange={(v) => setProfile(model.fleet.vehicle, { repairs: v })} suffix="$/van/yr" hint="unscheduled — the allowance a tender panel will look for" />
        </div>
        <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: 12, lineHeight: 1.6 }}>
          At {n0(model.fuel.kmPerDay)} km a day each van covers <b>{n0(V.kmPerVanYear)} km</b> a year, so servicing runs on the
          {' '}{V.perVan.servicesPerYear.toFixed(1)} services a year the <b>{veh.serviceIntervalMonths}-month</b> interval forces
          — the distance interval would only call for {(V.kmPerVanYear / Math.max(1, Number(veh.serviceIntervalKm))).toFixed(1)}.
          Per van per year: {$0(V.perVan.total)}.
        </div>
        <Subtotal value={V.total} of={c.total} />
      </div>

      {/* fuel */}
      <div style={card}>
        <h3 style={sectionTitle}><Fuel size={13} /> Fuel — litres per 100 km, honestly</h3>
        <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.55 }}>
          The ADR combined figure is close to a highway average and a CBD delivery van never sees it: it stops every
          hundred metres, restarts a cold engine all morning and crawls between blocks. So the model states the ADR number,
          applies a stop-start uplift to it, and adds idling on top — idling burns litres at zero kilometres, so it can
          never be expressed as an L/100 km figure at all.
        </div>
        <div style={grid(160)}>
          <Field title="Km per van per day" value={model.fuel.kmPerDay} onChange={(v) => setIn('fuel', { kmPerDay: v })} suffix="km"
            hint={sh ? `live: mean ${n1(sh.kmMean)}, median ${n1(sh.kmMedian)} (${n0(sh.kmSample)} shifts)` : null} />
          <Field title="ADR combined" value={veh.l100} onChange={(v) => setProfile(model.fleet.vehicle, { l100: v })} suffix="L/100km" />
          <Field title="Stop-start uplift" value={model.fuel.stopStartUpliftPct} onChange={(v) => setIn('fuel', { stopStartUpliftPct: v })} suffix="%" hint="traffic, kerb-to-door, cold restarts" />
          <Field title="Idle hours per day" value={model.fuel.idleHoursPerDay} onChange={(v) => setIn('fuel', { idleHoursPerDay: v })} suffix="h" hint="queues, loading, jams" />
          <Field title="Idle consumption" value={model.fuel.idleLitresPerHour} onChange={(v) => setIn('fuel', { idleLitresPerHour: v })} suffix="L/h" />
          <Field title="Diesel" value={model.fuel.dieselPerLitre} onChange={(v) => setIn('fuel', { dieselPerLitre: v })} suffix="$/L" hint="pump price, not the terminal gate price" />
        </div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14, fontSize: '.8rem' }}>
          <div><span style={label}>Effective driving</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n1(F.effective)}</b> L/100km</div>
          <div><span style={label}>Litres per van per day</span><b style={{ fontFamily: 'var(--font-mono)' }}>{F.litresPerDay.toFixed(2)}</b> ({F.drivingLitresPerDay.toFixed(2)} driving + {F.idleLitresPerDay.toFixed(2)} idle)</div>
          <div><span style={label}>All-in</span><b style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{n1(F.allInL100)}</b> L/100km</div>
          <div><span style={label}>Per van per day</span><b style={{ fontFamily: 'var(--font-mono)' }}>{$2(F.costPerDay)}</b></div>
          <div><span style={label}>Fleet litres a year</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n0(F.litresPerYear)}</b></div>
        </div>
        <Subtotal value={F.total} of={c.total} />
      </div>

      {/* equipment */}
      <div style={card}>
        <h3 style={sectionTitle}><Package size={13} /> Equipment & road costs</h3>
        <div style={grid(160)}>
          <Field title="Mobile per driver" value={model.equipment.mobilePerMonth} onChange={(v) => setIn('equipment', { mobilePerMonth: v })} suffix="$/month" />
          <Field title="Trolley" value={model.equipment.trolleyCost} onChange={(v) => setIn('equipment', { trolleyCost: v })} suffix="$" />
          <Field title="Trolley life" value={model.equipment.trolleyLifeYears} onChange={(v) => setIn('equipment', { trolleyLifeYears: v })} suffix="years" />
          <Field title="Parking & tolls" value={model.equipment.parkingTollsPerDay} onChange={(v) => setIn('equipment', { parkingTollsPerDay: v })} suffix="$/driver day" hint="the CBD line that never shows up in a per-parcel rate" />
          <Field title="Fines" value={model.equipment.finesPerVanPerYear} onChange={(v) => setIn('equipment', { finesPerVanPerYear: v })} suffix="$/van/yr" hint="what we do not recover from drivers" />
        </div>
        <div style={{ marginTop: 14 }}>
          {(model.equipment.rows || []).map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input className="input" style={{ flex: '2 1 200px' }} value={r.label} onChange={(e) => setEquip(i, { label: e.target.value })} />
              <input className="input" style={{ flex: '1 1 110px', fontFamily: 'var(--font-mono)' }} type="number" value={r.perDriverPerYear}
                onChange={(e) => setEquip(i, { perDriverPerYear: e.target.value })} />
              <span style={{ fontSize: '.7rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>$/driver/yr</span>
              <button className="btn btn-sm" onClick={() => dropEquip(i)}><Trash2 size={12} /></button>
            </div>
          ))}
          <button className="btn btn-sm" onClick={addEquip}>+ Add an item</button>
        </div>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, minWidth: 340 }}>
            <tbody>
              {[['Mobiles', E.mobiles], ['Trolleys', E.trolleys], ['Parking & tolls', E.parkingTolls], ['Fines', E.fines],
                ...E.rows.map((r) => [r.label, r.total])].map(([k, v], i) => (
                <tr key={i}><td style={td}>{k}</td><td style={num}>{$0(v)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <Subtotal value={E.total} of={c.total} />
      </div>

      {/* overheads */}
      <div style={card}>
        <h3 style={sectionTitle}><Building2 size={13} /> Indirect & overhead</h3>
        <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.55 }}>
          Nothing here is delivered by a driver, and all of it has to be carried by the four priced lines.
          These are the figures to replace first — they are seeded as placeholders, not as your accounts.
        </div>
        {(model.overheads.rows || []).map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input className="input" style={{ flex: '2 1 220px' }} value={r.label} onChange={(e) => setOverhead(i, { label: e.target.value })} />
            <input className="input" style={{ flex: '1 1 120px', fontFamily: 'var(--font-mono)' }} type="number" value={r.annual}
              onChange={(e) => setOverhead(i, { annual: e.target.value })} />
            <span style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>$/yr</span>
            <button className="btn btn-sm" onClick={() => dropOverhead(i)}><Trash2 size={12} /></button>
          </div>
        ))}
        <button className="btn btn-sm" onClick={addOverhead}>+ Add an overhead</button>
        <Subtotal value={c.overheads.total} of={c.total} />
      </div>

      <div style={{ ...card, borderColor: 'var(--hp-gold)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 800, fontSize: '.9rem' }}>Total operating cost</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '1.3rem', color: 'var(--hp-gold)' }}>{$0(c.total)}</span>
      </div>
    </div>
  );
}

// ── Vehicles ─────────────────────────────────────────────────────────────────
function VehicleTab({ model, compare, actuals, setIn, setProfile }) {
  const keys = Object.keys(model.fleet.profiles);
  const mix = (actuals && actuals.fleet && actuals.fleet.mix) || {};
  const inService = (k) => {
    const want = k === 'hiace' ? 'HIACE' : 'DELIVER 9';
    return Object.entries(mix).filter(([name]) => name.toUpperCase().includes(want)).reduce((s, [, v]) => s + v, 0);
  };

  const rows = [
    ['Capital cost per van', (r) => $0(r.costs.vehicles.profile.capex), true],
    ['Residual at end of term', (r) => `${r.costs.vehicles.profile.residualPct}% · ${$0(r.costs.vehicles.perVan.residual)}`],
    ['Depreciation', (r) => $0(r.costs.vehicles.perVan.depreciation)],
    ['Finance interest', (r) => $0(r.costs.vehicles.perVan.interest)],
    ['Registration & CTP', (r) => $0(r.costs.vehicles.perVan.regoCtp)],
    ['Insurance', (r) => $0(r.costs.vehicles.perVan.insurance)],
    ['Servicing', (r) => `${$0(r.costs.vehicles.perVan.servicing)} · ${r.costs.vehicles.perVan.servicesPerYear.toFixed(1)}/yr`],
    ['Tyres', (r) => $0(r.costs.vehicles.perVan.tyres)],
    ['Repairs & maintenance', (r) => $0(r.costs.vehicles.perVan.repairs)],
    ['Per van per year', (r) => $0(r.costs.vehicles.perVan.total), true],
    ['ADR combined', (r) => `${n1(r.costs.fuel.adr)} L/100km`],
    ['All-in consumption', (r) => `${n1(r.costs.fuel.allInL100)} L/100km`],
    ['Fuel per van per year', (r) => $0(r.costs.fuel.costPerVanYear)],
    ['Van + fuel per year', (r) => $0(r.costs.vehicles.perVan.total + r.costs.fuel.costPerVanYear), true],
  ];

  const totals = [
    ['Fleet — vehicles', (r) => $0(r.costs.vehicles.total)],
    ['Fleet — fuel', (r) => $0(r.costs.fuel.total)],
    ['Total operating cost', (r) => $0(r.costs.total), true],
    ['Parcel rate required', (r) => $4(r.pricing.lines[0].proposedRate)],
    ['Margin at current rates', (r) => pct0(r.pricing.currentMarginPct, 2), true],
  ];

  const best = keys.reduce((a, b) => (compare[a].costs.total <= compare[b].costs.total ? a : b));
  const delta = Math.abs(compare[keys[0]].costs.total - compare[keys[1] || keys[0]].costs.total);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...card, borderColor: 'var(--hp-gold)' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Truck size={18} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.86rem', lineHeight: 1.6 }}>
            Both options run through the identical model — same drivers, same days, same volumes — so the only thing that moves is the van.
            On the numbers as they stand the <b>{model.fleet.profiles[best].label}</b> is cheaper, by{' '}
            <b style={{ fontFamily: 'var(--font-mono)' }}>{$0(delta)}</b> a year across {model.fleet.vans} vans
            ({$0(delta / Math.max(1, Number(model.fleet.vans)))} per van).
            {delta / compare[best].costs.total < 0.01 && ' That is under one percent of the cost base — close enough that availability, lead time and downtime should decide it, not the price.'}
          </div>
        </div>
      </div>

      <div style={card}>
        <h3 style={sectionTitle}><Truck size={13} /> Per van, per year</h3>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={th} />
                {keys.map((k) => (
                  <th key={k} style={{ ...th, textAlign: 'right', color: model.fleet.vehicle === k ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>
                    {model.fleet.profiles[k].label}
                    <div style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, fontSize: '.66rem', color: 'var(--hp-dim)' }}>
                      {inService(k) > 0 ? `${inService(k)} in service today` : model.fleet.profiles[k].note}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([lbl, pick, strong]) => (
                <tr key={lbl}>
                  <td style={{ ...td, fontWeight: strong ? 800 : 400 }}>{lbl}</td>
                  {keys.map((k) => <td key={k} style={{ ...num, fontWeight: strong ? 800 : 400 }}>{pick(compare[k])}</td>)}
                </tr>
              ))}
              <tr><td style={{ ...td, paddingTop: 16 }} colSpan={keys.length + 1} /></tr>
              {totals.map(([lbl, pick, strong]) => (
                <tr key={lbl}>
                  <td style={{ ...td, fontWeight: strong ? 800 : 400 }}>{lbl}</td>
                  {keys.map((k) => <td key={k} style={{ ...num, fontWeight: strong ? 800 : 400, color: strong ? 'var(--hp-gold)' : undefined }}>{pick(compare[k])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {keys.map((k) => (
            <button key={k} className={`btn btn-sm ${model.fleet.vehicle === k ? 'btn-primary' : ''}`} onClick={() => setIn('fleet', { vehicle: k })}>
              Price the submission on the {model.fleet.profiles[k].label}
            </button>
          ))}
        </div>
      </div>

      {keys.map((k) => (
        <div key={k} style={{ ...card, borderColor: model.fleet.vehicle === k ? 'var(--hp-gold)' : 'var(--hp-border)' }}>
          <h3 style={sectionTitle}>{model.fleet.profiles[k].label}</h3>
          <div style={grid(150)}>
            <Field title="Capital cost" value={model.fleet.profiles[k].capex} onChange={(v) => setProfile(k, { capex: v })} suffix="$" />
            <Field title="Residual" value={model.fleet.profiles[k].residualPct} onChange={(v) => setProfile(k, { residualPct: v })} suffix="%" />
            <Field title="ADR combined" value={model.fleet.profiles[k].l100} onChange={(v) => setProfile(k, { l100: v })} suffix="L/100km" />
            <Field title="Service interval" value={model.fleet.profiles[k].serviceIntervalMonths} onChange={(v) => setProfile(k, { serviceIntervalMonths: v })} suffix="months" />
            <Field title="Service interval" value={model.fleet.profiles[k].serviceIntervalKm} onChange={(v) => setProfile(k, { serviceIntervalKm: v })} suffix="km" />
            <Field title="Cost per service" value={model.fleet.profiles[k].serviceCost} onChange={(v) => setProfile(k, { serviceCost: v })} suffix="$" />
            <Field title="Tyre set" value={model.fleet.profiles[k].tyreSetCost} onChange={(v) => setProfile(k, { tyreSetCost: v })} suffix="$" />
            <Field title="Tyre life" value={model.fleet.profiles[k].tyreLifeKm} onChange={(v) => setProfile(k, { tyreLifeKm: v })} suffix="km" />
            <Field title="Repairs" value={model.fleet.profiles[k].repairs} onChange={(v) => setProfile(k, { repairs: v })} suffix="$/yr" />
            <Field title="Rego & CTP" value={model.fleet.profiles[k].regoCtp} onChange={(v) => setProfile(k, { regoCtp: v })} suffix="$/yr" />
            <Field title="Insurance" value={model.fleet.profiles[k].insurance} onChange={(v) => setProfile(k, { insurance: v })} suffix="$/yr" />
          </div>
        </div>
      ))}

      <div style={{ ...card, fontSize: '.75rem', color: 'var(--hp-dim)', lineHeight: 1.6, display: 'flex', gap: 8 }}>
        <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--hp-amber)' }} />
        <span>
          Capital costs, residuals, service pricing and insurance are seeded as <b>assumptions</b>, not quotes.
          Before this goes into a tender, replace them with a dealer quote for {model.fleet.vans} units, a fleet insurance quote,
          and the servicing schedule from the dealer — the residual in particular swings the answer more than the sticker price does.
        </span>
      </div>
    </div>
  );
}

// ── Volumes & rates ──────────────────────────────────────────────────────────
function VolumeTab({ model, run, actuals, annual, setLine, setIn }) {
  const p = run.pricing;

  const seed = () => {
    if (!annual) return;
    // The year, as worked. Volumes come from the 12 months of wahr_history; the
    // delivery calendar comes with them, because how many days were worked is as
    // much a part of the cost as how many parcels crossed the dock.
    setIn('ops', { weekdayDays: annual.weekdays, saturdayDays: annual.saturdays });
    setLine('parcels', { volPerYear: Math.round(annual.articles) });
    setLine('depot', { volPerYear: Math.round(annual.depot.annual) });
    setLine('express', { volPerYear: Math.round(annual.express.annual) });
  };

  const seedShifts = () => {
    if (!actuals || !actuals.shifts) return;
    setIn('ops', {
      driversPerDay: Number(actuals.shifts.driversPerDay.toFixed(1)),
      hoursPerShift: Number(actuals.shifts.hoursMean.toFixed(2)),
    });
    setIn('fuel', { kmPerDay: Number(actuals.shifts.kmMean.toFixed(1)) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {annual && (
        <div style={card}>
          <h3 style={sectionTitle}><RefreshCw size={13} /> The year as worked — {annual.window.start} to {annual.window.end}</h3>
          <div style={{ ...grid(150), marginBottom: 12 }}>
            <div><span style={label}>Articles delivered</span><b style={{ fontFamily: 'var(--font-mono)', fontSize: '1.05rem' }}>{n0(annual.articles)}</b>
              <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)' }}>{n0(annual.articlesPerDay)} a delivery day</div></div>
            <div><span style={label}>Delivery days</span><b style={{ fontFamily: 'var(--font-mono)', fontSize: '1.05rem' }}>{n0(annual.deliveryDays)}</b>
              <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)' }}>{n0(annual.weekdays)} weekdays · {n0(annual.saturdays)} Saturdays</div></div>
            <div><span style={label}>Depot bags</span><b style={{ fontFamily: 'var(--font-mono)', fontSize: '1.05rem' }}>{n0(annual.depot.annual)}</b>
              <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)' }}>{n1(annual.depot.perDay)} a day over {annual.depot.days} days of feed</div></div>
            <div><span style={label}>2nd Wave Express</span><b style={{ fontFamily: 'var(--font-mono)', fontSize: '1.05rem' }}>{n0(annual.express.annual)}</b>
              <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)' }}>{n1(annual.express.perDay)} a day over {annual.express.days} days of feed</div></div>
          </div>
          <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.55, display: 'flex', gap: 8 }}>
            <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              Articles and the delivery calendar are the WAHR history, the same measure AP's own base data uses.
              Depot bags and Express come from feeds that only start in 2026, so their rate per day is carried across
              the {n0(annual.deliveryDays)} delivery days the year actually had. <b>Only {n0(annual.saturdays)} of those
              were Saturdays</b> — this is a Monday-to-Friday operation, and costing it as Mon–Sat would invent
              {' '}{n0(52 - annual.saturdays)} days of drivers, vans and diesel that never happened.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={seed}><RefreshCw size={13} /> Seed the year's volumes and calendar</button>
            {actuals && actuals.shifts && (
              <button className="btn btn-sm" onClick={seedShifts}>
                <RefreshCw size={13} /> Seed drivers, hours and km ({n1(actuals.shifts.driversPerDay)} a day · {n1(actuals.shifts.hoursMean)} h · {n1(actuals.shifts.kmMean)} km)
              </button>
            )}
          </div>
        </div>
      )}

      <div style={card}>
        <h3 style={sectionTitle}><Package size={13} /> The four priced lines</h3>
        <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 14, lineHeight: 1.55 }}>
          <b>Weight</b> decides how the required revenue is shared out. Seeded as the current rate itself, which spreads any
          uplift evenly — every line moves by the same percentage. Raise one line's weight to load it harder than the others.
          <b> Hold at current</b> means never quote below what AP pays today; the revenue that line would have given up is
          carried by the lines still free to move.
        </div>
        {LINE_DEFS.map((d) => {
          const cfg = model.lines[d.key];
          const line = p.lines.find((l) => l.key === d.key);
          return (
            <div key={d.key} style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 14, marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <div>
                  <span style={{ fontWeight: 800, fontSize: '.9rem' }}>{d.label}</span>
                  <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginLeft: 8 }}>{d.hint}</span>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.85rem' }}>
                  {line.currentRate >= 100 ? $2(line.currentRate) : $4(line.currentRate)}
                  <span style={{ color: 'var(--hp-dim)' }}> → </span>
                  <b style={{ color: 'var(--hp-gold)' }}>{line.proposedRate >= 100 ? $2(line.proposedRate) : $4(line.proposedRate)}</b>
                  <span style={{ color: 'var(--hp-dim)', fontSize: '.72rem' }}> · {$0(line.proposedRevenue)} a year</span>
                </div>
              </div>
              <div style={grid(150)}>
                <Field
                  title={d.key === 'doe' ? 'Service days a year' : `${d.unit}s a year`}
                  value={cfg.volPerYear} onChange={(x) => setLine(d.key, { volPerYear: x })}
                  hint={d.key === 'doe' ? '20 a period × 13' : `${n1(Number(cfg.volPerYear) / Math.max(1, run.volumes.days))} a delivery day`} />
                <Field title="Current AP rate" value={cfg.currentRate} onChange={(x) => setLine(d.key, { currentRate: x })} suffix={`$/${d.unit}`} />
                <Field title="Weight" value={cfg.weight} onChange={(x) => setLine(d.key, { weight: x })} hint="share of revenue this line carries" />
                {d.key === 'doe' && <Field title="School collections a year" value={cfg.collectionsPerYear} onChange={(x) => setLine(d.key, { collectionsPerYear: x })} />}
                {d.key === 'doe' && <Field title="Collection rate" value={cfg.collectionRate} onChange={(x) => setLine(d.key, { collectionRate: x })} suffix="$ each" />}
                {d.key === 'express' && <Field title="Unit price" value={cfg.grossRate} onChange={(x) => setLine(d.key, { grossRate: x })} suffix="$" hint="before AP's per-item adjustment" />}
                {d.key === 'express' && <Field title="Per-item adjustment" value={cfg.adjustment} onChange={(x) => setLine(d.key, { adjustment: x })} suffix="$" hint="net of this is what we are paid" />}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'flex-end', paddingBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.76rem' }}>
                    <input type="checkbox" checked={cfg.floorAtCurrent !== false} onChange={(e) => setLine(d.key, { floorAtCurrent: e.target.checked })} />
                    Hold at current rate or above
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.76rem' }}>
                    <input type="checkbox" checked={cfg.include !== false} onChange={(e) => setLine(d.key, { include: e.target.checked })} />
                    Include in the submission
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...card, fontSize: '.75rem', color: 'var(--hp-dim)', lineHeight: 1.6, display: 'flex', gap: 8 }}>
        <Info size={15} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          Express appears twice on purpose. AP pays the article rate on every delivered item including Express, and pays the
          2nd Wave line a second time on top — so Express volume sits inside the Parcels line as well as on its own.
          Night Sort is not priced here: it is a lump-sum variation delivered by sorters, not by these drivers or these vans,
          so neither its revenue nor its cost belongs in this model.
        </span>
      </div>
    </div>
  );
}
